/* ─────────────────────────────────────────────────────────────────────────────
   THE ONE OWNER of "how does a company spread an annual sales quota across months?"

   ★ PRINCIPLE 0. Before this file the rule was spelled THREE TIMES — runFullModel's
     monthlyNewACV block, runFullModel's commissionData block, and RevenueBuildPage's
     aeData preview. Measured 2026-09-15: the three were CHARACTER-IDENTICAL after
     normalising the receiver (one normalised string, set size 1), i.e. genuinely ONE rule
     in three spellings. ⛔ Fixing two of three would have reintroduced exactly the drift
     §6 warns about.

   ★★ QUOTA IS A CALENDAR-YEAR CONCEPT (Brian, 2026-09-15). EACH CALENDAR YEAR BOOKS ONE
     ANNUAL QUOTA. The pattern is COMPANY-WIDE — every rep uses it — and each calendar
     year's factors sum to 100%.
     ⇒ this is what the old per-rep design got wrong: it validated the factors to 100%
       ACROSS THE FORECAST WINDOW, so a 17-month window compressed one year's quota into
       seventeen months (measured: $600,000 booked where $850,000 was correct, -29.4%).

   THE STORED SHAPE (`ae_quota_settings.quota_distribution`, jsonb, NULLABLE, DEFAULTLESS):
       null                                         never answered  -> straight line
       { mode: 'straight_line' }                    the SENTINEL    -> explicitly straight line
       { mode: 'seasonal', factors: { 'YYYY-MM': <decimal> } }
   ⓘ Factors are DECIMALS (0.0833 = 8.33%), matching what the retired per-rep map stored.

   ⛔ RAMP IS NOT THIS FILE'S BUSINESS. `ramp_months` / `ramp_schedule` stay PER REP on
     `employees.quota_monthly_factors` (they describe a new hire's ramp, edited on the
     Payroll tab) and they multiply AFTER the amount this file returns, at all three call
     sites. The two are ORTHOGONAL — do not fold ramp in here.
   ───────────────────────────────────────────────────────────────────────────── */

const r2 = n => Math.round(n * 100) / 100;

/** The canonical tolerance for "this year's factors sum to 100%".
 *  ⓘ 1e-4 on a DECIMAL sum — i.e. one hundredth of a percentage point. It is the value the
 *  retired per-rep gate used (`Math.abs(sum - 1) >= 0.0001`), kept deliberately so the new
 *  rule is not quietly stricter or looser than the one operators already met. The SERVER
 *  guard uses the same number (see server.js) — THE WALL forbids importing this file there,
 *  so the constant is duplicated by necessity and the verify asserts the two agree. */
export const QUOTA_FACTOR_TOLERANCE = 0.0001;

/** Normalise whatever is stored into exactly one of the three states.
 *  ⛔ Never throws: this runs on a render path (the B5 K3 precedent). */
export function normalizeQuotaPattern(stored) {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return { mode: 'straight_line', factors: {} };
  }
  const raw = typeof stored === 'string' ? safeParse(stored) : stored;
  if (!raw || raw.mode !== 'seasonal') return { mode: 'straight_line', factors: {} };
  const factors = (raw.factors && typeof raw.factors === 'object' && !Array.isArray(raw.factors))
    ? raw.factors : {};
  return { mode: 'seasonal', factors };
}

function safeParse(s) { try { return JSON.parse(s); } catch { return null; } }

/** ★ THE ONE RULE. Returns the month's quota amount BEFORE ramp.
 *
 *  ⚠ THE UNCOVERED-MONTH ARM IS A DELIBERATE CHOICE, NOT AN OVERSIGHT.
 *  A seasonal month with no factor falls back to the straight-line rate AND is reported
 *  through `onUncovered`. The retired code did the same fallback SILENTLY, which is the
 *  filed §E1 defect ("a partial map silently switches distribution mid-window"). The fix
 *  is to keep the non-destructive number and remove the SILENCE — ⛔ booking 0 would wipe
 *  a real forecast to punish a data gap.
 *  ⓘ It is reachable only when the window GROWS past a saved pattern (the filed "Extend
 *    Forecast End" module), because the grid + the per-year guard cover the whole window
 *    at save time. */
export function monthlyQuotaAmount(annualQuota, pattern, month, onUncovered) {
  const q = parseFloat(annualQuota) || 0;
  if (pattern && pattern.mode === 'seasonal') {
    if (pattern.factors[month] !== undefined) {
      return r2(q * (parseFloat(pattern.factors[month]) || 0));
    }
    if (typeof onUncovered === 'function') onUncovered(month);
  }
  return r2(q / 12);
}

/** Group month keys by calendar year. Shared by the grid, both validators and the verify. */
export function factorsByYear(factors) {
  const out = {};
  Object.keys(factors || {}).forEach(m => {
    const y = String(m).slice(0, 4);
    (out[y] = out[y] || []).push(m);
  });
  Object.values(out).forEach(a => a.sort());
  return out;
}

/** ★ THE PER-CALENDAR-YEAR RULE, as a predicate. Returns [] when valid.
 *  Each year present must carry TWELVE months and sum to 1.0 within the tolerance.
 *  ⓘ Requiring 12 keys is what makes "per calendar year" enforceable WITHOUT knowing the
 *    forecast window at all — which is why the server can run the identical check. */
export function validateQuotaPattern(pattern) {
  const errs = [];
  if (!pattern || pattern.mode !== 'seasonal') return errs;
  const byYear = factorsByYear(pattern.factors);
  const years = Object.keys(byYear).sort();
  if (years.length === 0) {
    errs.push({ year: null, code: 'no_factors', message: 'Seasonal is selected but no monthly percentages have been entered.' });
    return errs;
  }
  years.forEach(y => {
    const months = byYear[y];
    if (months.length !== 12) {
      errs.push({ year: y, code: 'incomplete_year', count: months.length,
        message: `${y} has ${months.length} of 12 months. A calendar year must carry all twelve.` });
      return;
    }
    const sum = months.reduce((s, m) => s + (parseFloat(pattern.factors[m]) || 0), 0);
    if (Math.abs(sum - 1) >= QUOTA_FACTOR_TOLERANCE) {
      errs.push({ year: y, code: 'bad_sum', sum,
        message: `${y} sums to ${(sum * 100).toFixed(2)}% — each calendar year must sum to 100.00%.` });
    }
  });
  return errs;
}

/** ★★ THE GRID'S YEARS — FROM THE SEAM YEAR THROUGH THE LAST YEAR THE WINDOW TOUCHES.
 *  (Brian, 2026-09-15, after the walk.)
 *
 *  THE SEAM YEAR is the calendar year the FORECAST begins in — the year of the first month
 *  after the cutoff. ⛔ NOT the earliest year the model touches.
 *  ⇒ THE RULE IS DERIVED, ⛔ NEVER A FIXED COUNT: a tenant closing in January shows ONE year;
 *    tenant #2 (close 2026-07 → 2027-12) shows 2026 AND 2027.
 *
 *  ⚠ WHY THIS EXISTS: the first build used every year in `allMonths`, so cid=36 rendered 2021,
 *  2022 and 2023 — and 2021/2022 are ENTIRELY HISTORICAL. That is 24 input cells the engine can
 *  never read, because both engine loops skip `m <= cutoff` before any lookup.
 *
 *  ★ CLOSED MONTHS *WITHIN THE SEAM YEAR* STAY ENTERABLE, deliberately — that is D2's whole
 *    point: the operator must see the seam year reach 100% and know what is left for the
 *    forecast tail. ⛔ Do not "tidy" them away; they are the reason the year can sum to 100. */
export function quotaGridYears(cutoff, allMonths) {
  const ms = (allMonths || []).map(String).sort();
  const fc = cutoff ? ms.filter(m => m > cutoff) : ms;
  if (!fc.length) return [];
  const first = Number(fc[0].slice(0, 4));
  const last = Number(ms[ms.length - 1].slice(0, 4));
  const out = [];
  for (let y = first; y <= last; y++) out.push(String(y));
  return out;
}

/** Every month of the grid's years — the exact key set a pattern may carry. */
export function quotaGridMonths(cutoff, allMonths) {
  const out = [];
  quotaGridYears(cutoff, allMonths).forEach(y => {
    for (let i = 1; i <= 12; i++) out.push(`${y}-${String(i).padStart(2, '0')}`);
  });
  return out;
}

/** ★ PRUNE a factor map to the grid's years.
 *  ⛔ THIS IS WHAT MAKES THE THREE AGREE: if the grid stops showing a year, the client
 *    validator must not still demand 100% for it, and the DEPLOYED server validator must not be
 *    handed a year the grid can no longer repair. Pruning at the SAVE boundary is the one place
 *    all three meet.
 *  ⓘ It drops only years the operator can no longer see, and those years are INERT: the engine
 *    reads no month at or before the cutoff. */
export function pruneToGrid(factors, cutoff, allMonths) {
  const keep = new Set(quotaGridMonths(cutoff, allMonths));
  const out = {};
  Object.keys(factors || {}).forEach(m => { if (keep.has(m)) out[m] = factors[m]; });
  return out;
}

/** A per-calendar-year default that its OWN validator accepts, at any window length.
 *  ★ THIS REPLACES THE RETIRED PER-REP SEEDER, which was broken independently: it indexed
 *    `forecastMonths` by POSITION and applied `r2` — a MONEY rounder, two decimals — to a
 *    FRACTION, producing 99.63% at twelve months, 74.97% at nine and 139.63% at seventeen.
 *    Its own gate blocked every one of them, which is why the seasonal path could not be
 *    saved at all. Here the remainder carries FULL PRECISION and lands on December. */
export function seedEvenYear(year) {
  const f = {};
  const per = Math.round((1 / 12) * 1e6) / 1e6;
  let acc = 0;
  for (let i = 1; i <= 12; i++) {
    const m = `${year}-${String(i).padStart(2, '0')}`;
    if (i < 12) { f[m] = per; acc += per; } else { f[m] = Math.round((1 - acc) * 1e6) / 1e6; }
  }
  return f;
}
